/**
 * Utilitaires pour la gestion des permissions et visibilité des menus selon le rôle
 */

/**
 * Détermine les onglets visibles selon le rôle actif et son statut
 * @param {string} currentRole - Rôle actif de l'utilisateur
 * @param {Object} currentRoleStatus - Statut du rôle actif
 * @param {Object} fallbackData - Données de fallback (subs, tenantPass)
 * @returns {Array} Liste des clés d'onglets visibles
 */
export function getVisibleTabKeys(currentRole, currentRoleStatus, fallbackData = {}) {
  const baseKeys = ["home", "achat", "profile"]; // Toujours visibles
  
  if (!currentRole) return baseKeys;
  
  const { subs, tenantPass } = fallbackData;
  
  // Si on a les statuts des rôles (nouvelle API)
  if (currentRoleStatus) {
    const isActive = currentRoleStatus.status === "active";
    
    if (currentRole === "tenant" && isActive) {
      return [...baseKeys, "location"];
    } else if ((currentRole === "owner" || currentRole === "vendor") && isActive) {
      return [...baseKeys, "add", "offers"];
    }
  }
  
  // Fallback vers l'ancienne logique
  const subActive = subs?.active === true;
  const passActive = tenantPass?.activated === true;
  
  if (currentRole === "tenant" && passActive) {
    return [...baseKeys, "location"];
  } else if ((currentRole === "owner" || currentRole === "vendor") && subActive) {
    return [...baseKeys, "add", "offers"];
  }
  
  return baseKeys;
}

/**
 * Détermine les liens de navigation visibles selon le rôle actif
 * @param {string} currentRole - Rôle actif de l'utilisateur
 * @param {Object} currentRoleStatus - Statut du rôle actif
 * @param {Object} fallbackData - Données de fallback
 * @returns {Array} Liste des clés de liens visibles
 */
export function getVisibleHeaderKeys(currentRole, currentRoleStatus, fallbackData = {}) {
  const baseKeys = ["home", "achat", "profile"]; // Toujours visibles
  
  if (!currentRole) return baseKeys;
  
  const { subs, tenantPass } = fallbackData;
  
  // Si on a les statuts des rôles (nouvelle API)
  if (currentRoleStatus) {
    const isActive = currentRoleStatus.status === "active";
    
    if (currentRole === "tenant" && isActive) {
      return [...baseKeys, "location"];
    } else if ((currentRole === "owner" || currentRole === "vendor") && isActive) {
      return [...baseKeys, "add", "offers"];
    }
  }
  
  // Fallback vers l'ancienne logique
  const subActive = subs?.active === true;
  const passActive = tenantPass?.activated === true;
  
  if (currentRole === "tenant" && passActive) {
    return [...baseKeys, "location"];
  } else if ((currentRole === "owner" || currentRole === "vendor") && subActive) {
    return [...baseKeys, "add", "offers"];
  }
  
  return baseKeys;
}

/**
 * Vérifie si un utilisateur peut accéder à une fonctionnalité
 * @param {string} currentRole - Rôle actif de l'utilisateur
 * @param {Object} currentRoleStatus - Statut du rôle actif
 * @param {string} feature - Fonctionnalité à vérifier
 * @returns {boolean} True si l'utilisateur peut accéder
 */
export function canAccessFeature(currentRole, currentRoleStatus, feature) {
  if (!currentRole) return false;
  
  const isActive = currentRoleStatus?.status === "active";
  
  switch (feature) {
    case "location":
      return currentRole === "tenant" && isActive;
    case "add":
    case "offers":
      return (currentRole === "owner" || currentRole === "vendor") && isActive;
    case "profile":
    case "home":
    case "achat":
      return true; // Toujours accessible
    default:
      return false;
  }
}

/**
 * Obtient le label du rôle en français
 * @param {string} role - Rôle à traduire
 * @returns {string} Label en français
 */
export function getRoleLabel(role) {
  const labels = {
    tenant: "Locataire",
    owner: "Propriétaire",
    vendor: "Vendeur",
    admin: "Administrateur"
  };
  return labels[role] || role;
}

/**
 * Obtient la couleur associée à un rôle
 * @param {string} role - Rôle
 * @returns {string} Classes CSS de couleur
 */
export function getRoleColor(role) {
  const colors = {
    tenant: "bg-green-100 text-green-800 border-green-200",
    owner: "bg-orange-100 text-orange-800 border-orange-200",
    vendor: "bg-blue-100 text-blue-800 border-blue-200",
    admin: "bg-purple-100 text-purple-800 border-purple-200"
  };
  return colors[role] || "bg-gray-100 text-gray-800 border-gray-200";
}
